import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Container, Grid, CircularProgress, Typography, Box, Button } from '@mui/material';
import { withStyles } from '@mui/styles';
import { FormProvider, useFieldArray, useForm, Controller } from 'react-hook-form';
import AddIcon from '@mui/icons-material/Add';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import _ from 'lodash';
import FlagIcon from '@mui/icons-material/Flag';
import { Alert as MuiAlert } from '@mui/lab';
import ExpenseButtons from '../Components/Expense/ExpenseButtons';
import ExpenseEntryHeader from '../Components/Expense/ExpenseEntryHeader';
import ExpenseReceiptUploadForm from '../Components/Expense/ExpenseReceiptUploadForm';
import {
    createPendingCCExpense,
    updateExpense,
    updatePendingCCExpense,
    getExpenseById,
    getPendingCCExpenseById,
    getAllCreditCards,
    getExpenseCategories,
    getStatusesAllowed,
} from '../Services/ExpenseService';
import { getDivisions } from '../Services/Common';
import { formatErrorMessage } from '../Utils/ErrorMessage';
import { uploadImages } from '../Utils/UploadImages';
import AddExpenseTab from '../Components/Expense/Tabs/AddExpenseTab';
import ExpenseTabs from '../Components/Expense/Tabs/ExpenseTabs';
import ExpenseTab from '../Components/Expense/Tabs/ExpenseTab';
import ExpenseTabLabel from '../Components/Expense/Tabs/ExpenseTabLabel';
import ExpenseTabPanel from '../Components/Expense/Tabs/ExpenseTabPanel';
import BookingDetailsForm from '../Components/Expense/BookingDetailsForm';
import ExpenseSalesforceActionButton from '../Components/Expense/ExpenseSalesforceActionButton';
import PreCodedExpenseDetailsForm from '../Components/Expense/PreCodedExpenseDetailsForm';
import Alert from '../Components/Alert';
import ExpenseSummary from '../Components/Expense/ExpenseSummary';
import InternalNotesForm from '../Components/Expense/InternalNotesForm';
import ReasonForSplittingForm from '../Components/Expense/ReasonForSplittingForm';
import AdditionalExpenseDetails from '../Components/Expense/AdditionalExpenseDetails';
import { AdminAuthContext } from '../../Admin/Contexts/AdminAuthContext';
import { PreCodedStatusEnum } from '../Components/Expense/PreCodedStatus.enum';
import { ExpenseStatusEnum } from '../Components/Expense/ExpenseStatus.enum';
import { ExpenseClassEnum } from '../Components/Expense/ExpenseClass.enum';
import ExpenseNeedsReviewAlert from '../Components/Expense/ExpenseNeedsReviewAlert';
import { COLORS } from '../../utils/Application_Constants';
import { isExpenseViewOnlyByStatus } from '../Utils/ExpenseUtil';
import { ExpenseCategoryEnum } from '../Components/Expense/ExpenseCategory.enum';
import { ExpenseTypeEnum } from '../Components/Expense/ExpenseType.enum';

const styles = (theme) => ({
    ContainerRoot: {
        [theme.breakpoints.down('md')]: {
            paddingLeft: '16px',
            paddingRight: '16px',
        },
        paddingTop: '1.5rem',
        paddingBottom: '5rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '40px',
    },
    formGridContainer: {
        gap: 32,
    },
    summary: {
        position: 'sticky',
        top: '5rem',
        paddingTop: '.5rem',
    },
    viewBookingButton: {
        minWidth: 'auto',
        padding: '4px 8px',
        fontSize: '0.75rem',
    },
    iconContainer: {
        position: 'absolute',
        top: 8,
        right: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        zIndex: 10,
    },
    tabPanel: {
        position: 'relative',
    },
    alertContainer: {
        marginTop: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            marginTop: theme.spacing(4),
        },
        paddingLeft: '.5rem',
    },
});

const defaultPendingExpense = {
    label: 'Expense',
    amount: '',
    billType: '',
    bookingId: null,
    category: '',
    clientId: '',
    clinicianFirstName: '',
    clinicianId: '',
    clinicianLastName: '',
    accountingProcessingNotes: '',
    creditCardId: '',
    currency: 'USD',
    division: '',
    merchantName: '',
    serviceFee: '',
    state: '',
    totalExpense: '',
    type: '',
    status: 'Unassigned',
    salespersonName: '',
    recruiterName: '',
    payDate: null,
    expenseClass: 'Normal',
    splitTransactionComment: '',
    bookingType: '',
    bookingDivision: '',
    bookingCategory: '',
    sgaType: '',
    sgaDivision: '',
    sgaCategory: '',
};

const defaultCCExpense = {
    label: 'Expense',
    amount: '',
    billType: '',
    bookingId: null,
    category: '',
    clientId: '',
    clinicianFirstName: '',
    clinicianId: '',
    clinicianLastName: '',
    accountingProcessingNotes: '',
    creditCardId: '',
    currency: 'USD',
    division: '',
    merchantName: '',
    serviceFee: '',
    state: '',
    totalExpense: '',
    type: '',
    status: 'New',
    salespersonName: '',
    recruiterName: '',
    payDate: null,
    expenseClass: 'IngestedCCTransaction',
    splitTransactionComment: '',
    bookingType: '',
    bookingDivision: '',
    bookingCategory: '',
    sgaType: '',
    sgaDivision: '',
    sgaCategory: '',
    highPriority: false,
};

const mergeFields = {
    creditCardId: 'Ingested',
    transactionDate: 'Ingested',
    amount: 'Ingested',
    merchantName: 'Ingested',
    division: 'Pending CC/Pre-Code',
    category: 'Pending CC/Pre-Code',
    type: 'Pending CC/Pre-Code',
    startDate: 'Pending CC/Pre-Code',
    endDate: 'Pending CC/Pre-Code',
    bookingId: 'Pending CC/Pre-Code',
    billType: 'Pending CC/Pre-Code',
    clinicianId: 'Pending CC/Pre-Code',
    clinicianFirstName: 'Pending CC/Pre-Code',
    clinicianLastName: 'Pending CC/Pre-Code',
    state: 'Pending CC/Pre-Code',
};

const PreCoded = (props) => {
    const { classes, mode } = props;
    const [isLoading, setIsLoading] = useState(false);
    const [divisions, setDivisions] = useState([]);
    const [divisionsSGA, setDivisionsSGA] = useState([]);
    const [statusesAllowed, setStatusesAllowed] = useState([]);
    const { id } = useParams();
    const [alert, setAlert] = useState();
    const history = useHistory();
    const defaultValues =
        mode == 'cc'
            ? {
                  fileUploads: [{ newFiles: [], existingFiles: [] }],
                  expenseEntries: [defaultCCExpense],
              }
            : {
                  fileUploads: [{ newFiles: [], existingFiles: [] }],
                  expenseEntries: [defaultPendingExpense],
              };
    const methods = useForm({ shouldUnregister: false, defaultValues });
    const {
        handleSubmit,
        reset,
        control,
        getValues,
        watch,
        setValue,
        setError,
        clearErrors,
        formState: { errors },
    } = methods;
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const backLink = id ? 'All Expenses' : 'Expense Types';
    const title = mode === 'cc' ? 'Credit Card Transaction' : 'Pending CC Expense Entry';
    const expensesPath = id
        ? mode === 'pending-cc'
            ? '/expenses/pending-cc/review'
            : '/expenses/review'
        : '/expenses';
    const { isExpenseSystemAdministratorUser, isExpenseAdminUser, isClinicianReimEntryUser } =
        useContext(AdminAuthContext);
    const isViewOnly = !(isExpenseSystemAdministratorUser || isExpenseAdminUser);
    const watchExpenseEntries = watch(`expenseEntries`);
    const [originalExpenseEntries, setOriginalExpenseEntries] = useState([]);
    const [originalPrimaryExpense, setOriginalPrimaryExpense] = useState();
    const isAnyExpensePosted = useMemo(() => {
        if (mode === 'cc' && originalExpenseEntries?.length > 0) {
            return originalExpenseEntries.some((entry) => entry.status === 'Posted');
        }
        return false;
    }, [originalExpenseEntries]);
    const isAllExpensePosted = useMemo(() => {
        if (mode === 'cc' && originalExpenseEntries?.length > 0) {
            return !originalExpenseEntries.some((entry) => entry.status !== 'Posted');
        }
        return false;
    }, [originalExpenseEntries]);
    const [isSGA, setIsSGA] = useState([false, false, false, false, false]);
    const [removeTabs, setRemoveTabs] = useState(false);
    const [expenseCategoriesSGA, setExpenseCategoriesSGA] = useState(null);
    const [expenseCategoriesNonSGA, setExpenseCategoriesNonSGA] = useState(null);
    const [allCreditCards, setallCreditCards] = useState([]);
    const [showBalanceError, setShowBalanceError] = useState(false);
    const [assignmentsByIndex, setAssignmentsByIndex] = useState({});
    const hasErrors = !!Object.keys(methods.formState.errors).length;
    const {
        fields: expenseEntries,
        append,
        remove,
    } = useFieldArray({
        control, // control props comes from useForm (optional: if you are using FormContext)
        name: 'expenseEntries', // unique name for your Field Array
        keyName: 'expenseEntryId',
    });

    const handleAssignmentChange = (index, assignment) => {
        setAssignmentsByIndex((prev) => ({
            ...prev,
            [index]: assignment,
        }));
    };

    useEffect(() => {
        Promise.all([
            getDivisions(false, true),
            getStatusesAllowed('IngestedCCTransaction' /* Anything other than Reimbursement */),
            getAllCreditCards(),
            getExpenseCategories('CreditCard'),
            getExpenseCategories('SGA'),
        ])
            .then(async ([divsSGA, statuses, cc, catsNonSGA, catsSGA]) => {
                setDivisions(divsSGA.filter((val) => val.isSGA === false));
                setDivisionsSGA(divsSGA);
                setallCreditCards(cc);
                setExpenseCategoriesNonSGA(catsNonSGA);
                setExpenseCategoriesSGA(catsSGA);
                setStatusesAllowed(statuses);
            })
            .catch((error) => {
                console.error(error);
            });
        if (id) {
            fetchExpenseById();
        }
    }, []);

    const resetImages = (index) => {
        // Resort/Reset Images
        const fileUploads = getValues(`fileUploads`);
        fileUploads.splice(index, 1);
        setValue(`fileUploads`, fileUploads);
    };

    // This was added to address a rendering issue
    // It turns off the TabPanel Display removes the item and then turns back on the display
    useEffect(() => {
        if (removeTabs) {
            resetIsSGA(activeTabIndex);
            resetImages(activeTabIndex);
            remove(activeTabIndex);
            setActiveTabIndex(activeTabIndex - 1);
            setRemoveTabs(false);
        }
    }, [removeTabs]);

    const fetchExpenseById = async () => {
        try {
            setIsLoading(true);
            // TODO: Get all split/duplicate expense entires instead of singular once API is complete
            const res =
                mode === 'cc' ? await getExpenseById(id) : await getPendingCCExpenseById(id);
            const listExpenses = [];
            const aOriginalExpenseEntries = [];
            const aFileUploads = [];
            let selectedTabIndex = 0;
            if (mode === 'cc') {
                if (res.primaryExpenseId && res.primaryExpense) {
                    // child split
                    listExpenses.push(res.primaryExpense);
                    for (let i = 0; i < res.primaryExpense.splitExpenses.length; i++) {
                        const listExpensesIndex = listExpenses.push(
                            res.primaryExpense.splitExpenses[i]
                        );
                        if (Number(res.primaryExpense.splitExpenses[i].id) === Number(id)) {
                            selectedTabIndex = listExpensesIndex - 1;
                        }
                    }
                } else {
                    listExpenses.push(res);
                    for (let i = 0; i < res.splitExpenses.length; i++) {
                        listExpenses.push(res.splitExpenses[i]);
                    }
                }

                listExpenses[0].splitExpenses = [];
            } else {
                if (res.primaryPreCodedExpenseId && res.primaryPreCodedExpense) {
                    // child split
                    let childIndex = 0;
                    listExpenses.push(res.primaryPreCodedExpense);
                    for (
                        let i = 0;
                        i < res.primaryPreCodedExpense.splitPreCodedExpenses.length;
                        i++
                    ) {
                        const listExpensesIndex = listExpenses.push(
                            res.primaryPreCodedExpense.splitPreCodedExpenses[i]
                        );
                        if (res.primaryPreCodedExpense.splitPreCodedExpenses[i].id == id) {
                            childIndex = listExpensesIndex - 1;
                        }
                    }
                    setActiveTabIndex(childIndex);
                } else {
                    listExpenses.push(res);
                    for (let i = 0; i < res.splitPreCodedExpenses.length; i++) {
                        listExpenses.push(res.splitPreCodedExpenses[i]);
                    }
                }

                listExpenses[0].splitPreCodedExpenses = [];
            }

            for (let i = 0; i < listExpenses.length; i++) {
                // Sets expenseEntries[0] backup defaults
                if (listExpenses[i].expenseClass === ExpenseClassEnum.SalesGeneralAndAdmin) {
                    listExpenses[i].bookingType = '';
                    listExpenses[i].bookingDivision = '';
                    listExpenses[i].bookingCategory = '';
                    listExpenses[i].bookingBillType = '';
                    listExpenses[i].sgaType = listExpenses[i].type;
                    listExpenses[i].sgaDivision = listExpenses[i].division;
                    listExpenses[i].sgaCategory = listExpenses[i].category;
                    listExpenses[i].sgaBillType = listExpenses[i].billType;
                    isSGA[i] = true;
                } else {
                    listExpenses[i].bookingType = listExpenses[i].type;
                    listExpenses[i].bookingDivision = listExpenses[i].division;
                    listExpenses[i].bookingCategory = listExpenses[i].category;
                    listExpenses[i].bookingBillType = listExpenses[i].billType;
                    listExpenses[i].sgaType = '';
                    listExpenses[i].sgaDivision = '';
                    listExpenses[i].sgaCategory = '';
                    listExpenses[i].sgaBillType = '';
                    isSGA[i] = false;
                }
                let formattedValues;

                if (listExpenses[i].matchedId) {
                    const matchedExpense =
                        mode === 'cc'
                            ? await getExpenseById(listExpenses[i].matchedId)
                            : await getPendingCCExpenseById(listExpenses[i].matchedId);
                    const matchedValues = mergeMatchedValues(matchedExpense, listExpenses[i]);
                    formattedValues = formatResponse(matchedValues);
                } else {
                    formattedValues = formatResponse(listExpenses[i]);
                }
                // Reset back to Null as empty string on the as the default was causing issues
                formattedValues.payDate =
                    _.isString(formattedValues.payDate) && formattedValues.payDate.length === 0
                        ? null
                        : formattedValues.payDate.substring(0, 10);

                aOriginalExpenseEntries.push({
                    ...formattedValues,
                    label: listExpenses[i].label ? listExpenses[i].label : `Expense ${i}`,
                });

                if (mode === 'cc') {
                    if (
                        listExpenses[i] &&
                        listExpenses[i].expenseImages &&
                        listExpenses[i].expenseImages.length >= 0
                    ) {
                        aFileUploads.push({
                            newFiles: [],
                            existingFiles: listExpenses[i].expenseImages,
                        });
                    } else {
                        aFileUploads.push({ newFiles: [], existingFiles: [] });
                    }
                } else if (
                    listExpenses[i] &&
                    listExpenses[i].preCodedExpenseImages &&
                    listExpenses[i].preCodedExpenseImages.length >= 0
                ) {
                    aFileUploads.push({
                        newFiles: [],
                        existingFiles: listExpenses[i].preCodedExpenseImages,
                    });
                } else {
                    aFileUploads.push({ newFiles: [], existingFiles: [] });
                }
            }

            setIsSGA(isSGA);
            setOriginalExpenseEntries(aOriginalExpenseEntries);
            setOriginalPrimaryExpense(aOriginalExpenseEntries[0]);
            setActiveTabIndex(selectedTabIndex);
            const defaultValues = {
                fileUploads: aFileUploads,
                expenseEntries: aOriginalExpenseEntries,
            };
            reset(defaultValues);
        } catch (error) {
        } finally {
            setIsLoading(false);
        }
    };

    const mergeMatchedValues = (matchedExpense, defaultExpense) =>
        Object.fromEntries(
            Object.entries(mergeFields).map(([key, value]) => {
                let mergedValue = [key];
                const sourceOfTruth = value === 'Ingested' ? matchedExpense : defaultExpense;

                if (
                    matchedExpense[key] &&
                    defaultExpense[key] &&
                    matchedExpense[key] !== defaultExpense[key]
                ) {
                    mergedValue = [...mergedValue, sourceOfTruth[key]];
                } else {
                    mergedValue = [...mergedValue, defaultExpense[key]];
                }

                return mergedValue;
            })
        );

    const formatResponse = (res) =>
        Object.fromEntries(
            Object.entries(res).map(([key, value]) => [key, value === null ? '' : value])
        );

    const clearAllHiddenErrors = () => {
        const aExpenseEntries = getValues('expenseEntries');
        aExpenseEntries.map((element, index) => {
            clearExpenseHiddenErrors(element, index);
        });
    };

    const clearExpenseHiddenErrors = (preCodedexpense, index) => {
        // Field 1: Transaction Date  transactionDate
        if (!preCodedexpense.category) {
            clearErrors(`expenseEntries[${index}].transactionDate`);
        }

        // Field 2: Start Date  startDate
        // Field 3: End Date  endDate
        if (
            !(
                preCodedexpense.category === ExpenseCategoryEnum.Travel &&
                !(
                    preCodedexpense.type === ExpenseTypeEnum.BaggageFee ||
                    preCodedexpense.type === ExpenseTypeEnum.Fuel ||
                    !preCodedexpense.type
                )
            )
        ) {
            clearErrors(`expenseEntries[${index}].startDate`);
            clearErrors(`expenseEntries[${index}].endDate`);
        }
    };

    const validateSubmit = async () => {
        if (mode === 'cc') {
            clearAllHiddenErrors();
        }

        handleSubmit(onSubmit)();
    };

    const validateSave = async () => {
        if (mode === 'cc') {
            clearAllHiddenErrors();
        }
        if (errors.balance) {
            setError('balance', {
                type: 'manual',
                message: 'The sum of the expenses does not match the current transaction amount.',
            });
            setShowBalanceError(true);
            return;
        }
        clearErrors('balance');
        setShowBalanceError(false);
        handleSubmit(onSave)();
    };

    const getStatusKey = (value) => {
        if (mode === 'cc') {
            return Object.keys(ExpenseStatusEnum).find(
                (key) => ExpenseStatusEnum[key].value === value
            );
        }
        return Object.keys(PreCodedStatusEnum).find(
            (key) => PreCodedStatusEnum[key].value === value
        );
    };

    const prepareExpense = (origExpense) => {
        // Do not modify the existing expense object, clone it
        const expense = JSON.parse(JSON.stringify(origExpense));
        expense.bookingId = parseInt(expense.bookingId, 10);
        expense.clientId = expense.clientId !== '' ? expense.clientId : null;
        expense.serviceFee = expense.serviceFee !== '' ? expense.serviceFee : null;
        expense.creditCardId = parseInt(expense.creditCardId, 10);
        if (expense.billType == 'ClinicianDeduction') {
            if (expense.category == '') {
                expense.category = null;
            }
            if (expense.type == '') {
                expense.type = null;
            }
            const date = new Date();
            if (expense.transactionDate == '' || expense.transactionDate == null) {
                expense.transactionDate = date.toISOString();
            }
        }

        const datesRequired =
            (expense.category === ExpenseCategoryEnum.Travel &&
                expense.type &&
                expense.type !== ExpenseTypeEnum.BaggageFee &&
                expense.type !== ExpenseTypeEnum.Fuel) ||
            (expense.expenseClass === ExpenseClassEnum.SalesGeneralAndAdmin &&
                expense.type === ExpenseTypeEnum.LodgingSecurityDeposit);
        if (!datesRequired) {
            expense.startDate = null;
            expense.endDate = null;
        }

        Object.keys(expense).forEach((i) => {
            if (_.isString(expense[i]) && expense[i].length === 0) {
                expense[i] = null;
            }
        });

        return expense;
    };

    const onSave = async (data) => {
        try {
            setIsLoading(true);
            const expenses = data.expenseEntries.map(prepareExpense);
            const transformExpense = transformToExpenseAPI(expenses);
            const expenseResponse =
                mode === 'cc'
                    ? await updateExpense(transformExpense.id, transformExpense)
                    : await updatePendingCCExpense(transformExpense.id, transformExpense);
            const fileUploads = getValues(`fileUploads`);

            try {
                await uploadImages(fileUploads, expenseResponse, mode);

                history.push({
                    pathname: '/expenses/submit',
                    state: {
                        listViewUrl: '/expenses/pending-cc/review',
                        expenseUrl: `/expenses/pending-cc/${expenseResponse.id}`,
                        type: 'save',
                    },
                });
            } catch (uploadError) {
                console.error(
                    'Error onSaveCC Pending CC Expense Entry for image upload: ',
                    uploadError
                );
                const message =
                    'Expense was saved, but image upload failed. Error message: ' +
                    formatErrorMessage(uploadError);
                setAlert({
                    message,
                    type: 'error',
                });
            }
        } catch (error) {
            console.error('Error onSaveCC Pending CC Expense Entry');
            reset({ expenseEntries: data.expenseEntries, fileUploads: data.fileUploads });
            const message = formatErrorMessage(error);
            setAlert({
                message,
                type: 'error',
            });
        } finally {
            setIsLoading(false);
        }
    };

    const transformToExpenseAPI = (expenses) => {
        const rtnExpense = expenses[0];

        if (mode === 'cc') {
            rtnExpense.splitExpenses = [];
            rtnExpense.expenseImages = [];
            // Copy to splitExpenses
            for (let i = 1; i < expenses.length; i++) {
                expenses[i].expenseImages = [];
                rtnExpense.splitExpenses.push(expenses[i]);
            }
        } else {
            rtnExpense.splitPreCodedExpenses = [];
            rtnExpense.preCodedExpenseImages = [];

            // Copy to splitPreCodedExpenses
            for (let i = 1; i < expenses.length; i++) {
                expenses[i].preCodedExpenseImages = [];
                rtnExpense.splitPreCodedExpenses.push(expenses[i]);
            }
        }
        return rtnExpense;
    };

    // Submit will only be used for mode pending
    const onSubmit = async (data) => {
        try {
            setIsLoading(true);
            const expenses = data.expenseEntries.map(prepareExpense);
            const transformExpense = transformToExpenseAPI(expenses);
            const expenseResponse = await createPendingCCExpense(transformExpense);
            const fileUploads = getValues(`fileUploads`);
            try {
                await uploadImages(fileUploads, expenseResponse, mode);

                history.push({
                    pathname: '/expenses/submit',
                    state: {
                        listViewUrl: '/expenses/pending-cc/review',
                        expenseUrl: `/expenses/pending-cc/${expenseResponse.id}`,
                        type: 'submit',
                    },
                });
            } catch (uploadError) {
                console.error(
                    'Error onSubmit Pending CC Expense Entry for image upload: ',
                    uploadError
                );
                const message =
                    'Expense was created, but image upload failed. Error message: ' +
                    formatErrorMessage(uploadError);
                setAlert({
                    message,
                    type: 'error',
                });
            }
        } catch (error) {
            console.error('Error onSubmit Pending CC Expense Entry');
            reset({ expenseEntries: data.expenseEntries, fileUploads: data.fileUploads });
            const message = formatErrorMessage(error);
            setAlert({
                message,
                type: 'error',
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddExpense = () => {
        const newExpense = {
            ...getValues('expenseEntries[0]'),
            label: 'New Expense',
            amount: '',
            billType: '',
            id: null,
            created: null,
            lastModified: null,
            status: mode == 'cc' ? 'New' : 'Unassigned',
            highPriority: false,
            salespersonName: '',
            recruiterName: '',
            description: '',
            previousVersionId: null,
        };

        const newFileUpload = {
            ...getValues('fileUploads[0]'),
        };

        if (newFileUpload) {
            if (newFileUpload.existingFiles && newFileUpload.existingFiles.length > 0) {
                if (mode === 'cc') {
                    newFileUpload.existingFiles = newFileUpload.existingFiles
                        .filter((file) => !file.isCombinedPdf)
                        .map((file) => ({
                            ...file,
                            expenseId: null, // Clear out the Existing ExpenseId
                        }));
                } else {
                    newFileUpload.existingFiles.forEach((file) => {
                        // Clear out the Existing PreCodedExpenseId
                        file.preCodedExpenseId = null;
                    });
                }
            }

            const cloneNewFiles = [];
            if (newFileUpload.newFiles && newFileUpload.newFiles.length > 0) {
                newFileUpload.newFiles.forEach((file) => {
                    const cloneNewFile = new File([file], file.name, { type: file.type });
                    // Regenerate the preview to make unique for the tab
                    Object.assign(cloneNewFile, {
                        preview: URL.createObjectURL(file),
                    });
                    cloneNewFiles.push(cloneNewFile);
                });
            }
            newFileUpload.newFiles = cloneNewFiles;

            const fileUploads = getValues(`fileUploads`);
            fileUploads.push(newFileUpload);
            setValue(`fileUploads`, fileUploads);
        }

        if (newExpense.hasOwnProperty('submittedDate')) {
            newExpense.submittedDate = null;
        }

        if (newExpense.hasOwnProperty('resubmittedDate')) {
            newExpense.resubmittedDate = null;
        }

        if (newExpense.hasOwnProperty('errors')) {
            delete newExpense.errors;
        }
        if (newExpense.hasOwnProperty('statusHistory')) {
            delete newExpense.statusHistory;
        }
        if (newExpense.hasOwnProperty('relatedErrors')) {
            delete newExpense.relatedErrors;
        }
        if (newExpense.hasOwnProperty('importedData')) {
            delete newExpense.importedData;
        }
        if (!newExpense.splitTransactionComment) {
            newExpense.splitTransactionComment = '';
        }
        if (isSGA[0]) {
            newExpense.sgaType = newExpense.type;
            newExpense.sgaDivision = newExpense.division;
            newExpense.sgaCategory = newExpense.category;
            newExpense.sgaBillType = newExpense.billType;
            newExpense.sgaExpenseClass = newExpense.expenseClass;
        } else {
            newExpense.bookingType = newExpense.type;
            newExpense.bookingDivision = newExpense.division;
            newExpense.bookingCategory = newExpense.category;
            newExpense.bookingBillType = newExpense.billType;
            newExpense.bookingExpenseClass = newExpense.expenseClass;
        }
        isSGA[expenseEntries.length] = isSGA[0];
        setIsSGA(isSGA);
        append(newExpense);
        setActiveTabIndex(expenseEntries.length);
    };

    const resetIsSGA = (index) => {
        // Resort/Reset isSGA flags
        if (index == 4) {
            isSGA[index] = false;
        } else {
            for (let indexLoop = index; indexLoop < isSGA.length; ++indexLoop) {
                if (isSGA[indexLoop + 1] !== undefined) {
                    if (isSGA[indexLoop] !== isSGA[indexLoop + 1]) {
                        isSGA[indexLoop] = isSGA[indexLoop + 1];
                    }
                }
            }
        }
        setIsSGA(isSGA);
    };

    const handleRemoveExpense = (index) => {
        // This was added to address a rendering issue
        // It turns off the TabPanel Display removes the item and then turns back on the display
        setActiveTabIndex(index);
        setRemoveTabs(true);
    };

    function handleFlagClick(onChange, value) {
        if (isViewOnly) {
            return;
        }

        return onChange(!value);
    }

    return (
        <Container maxWidth="xl" className={classes.ContainerRoot}>
            <FormProvider {...methods}>
                <Grid container direction="column" spacing={2}>
                    <ExpenseEntryHeader
                        title={title}
                        backLink={backLink}
                        expensesPath={expensesPath}
                    />
                    {isLoading ? (
                        <CircularProgress color="primary" />
                    ) : (
                        <>
                            <Grid item xs={12}>
                                <form onSubmit={handleSubmit(onSubmit)}>
                                    <Grid container spacing={4}>
                                        <Grid item sm={7} md={8} lg={9} xs={12}>
                                            <ExpenseTabs value={activeTabIndex}>
                                                {watchExpenseEntries.map((expense, index) => (
                                                    <ExpenseTab
                                                        key={index}
                                                        id={`expense-tab-${index}`}
                                                        label={
                                                            <ExpenseTabLabel
                                                                isActive={activeTabIndex === index}
                                                                index={index}
                                                                handleRemoveTab={() =>
                                                                    handleRemoveExpense(index)
                                                                }
                                                                setValue={setActiveTabIndex}
                                                                tab={expense}
                                                                mode={mode}
                                                                isViewOnly={isViewOnly}
                                                                originalPrimaryExpense={
                                                                    originalPrimaryExpense
                                                                }
                                                                isAnyExpensePosted={
                                                                    isAnyExpensePosted
                                                                }
                                                            />
                                                        }
                                                    />
                                                ))}
                                                {!isViewOnly &&
                                                    !(mode === 'cc' && isAnyExpensePosted) && (
                                                        <AddExpenseTab
                                                            label={<AddIcon color="primary" />}
                                                            disabled={
                                                                watchExpenseEntries.length === 5 ||
                                                                isExpenseViewOnlyByStatus(
                                                                    watchExpenseEntries[
                                                                        activeTabIndex
                                                                    ],
                                                                    { isClinicianReimEntryUser }
                                                                )
                                                            }
                                                            onClick={handleAddExpense}
                                                            id="expense-tab-add"
                                                        />
                                                    )}
                                            </ExpenseTabs>
                                            {!removeTabs && (
                                                <div>
                                                    {watchExpenseEntries.map((expense, index) => (
                                                        <ExpenseTabPanel
                                                            className={classes.tabPanel}
                                                            key={`${index}-panel`}
                                                            value={activeTabIndex}
                                                            index={index}
                                                            id={`expense-panel-${index}`}
                                                        >
                                                            <Box className={classes.iconContainer}>
                                                                <ExpenseSalesforceActionButton
                                                                    assignment={
                                                                        assignmentsByIndex[index]
                                                                    }
                                                                    expense={expense}
                                                                    originalClinicianId={
                                                                        originalExpenseEntries[
                                                                            index
                                                                        ]?.clinicianId
                                                                    }
                                                                    className={
                                                                        classes.viewBookingButton
                                                                    }
                                                                />
                                                                <Controller
                                                                    name={`expenseEntries[${index}].highPriority`}
                                                                    control={control}
                                                                    defaultValue={
                                                                        expense.highPriority
                                                                    }
                                                                    render={({
                                                                        onChange,
                                                                        value,
                                                                    }) => (
                                                                        <FlagIcon
                                                                            fontSize="large"
                                                                            className={
                                                                                classes.flagIcon
                                                                            }
                                                                            onClick={() =>
                                                                                handleFlagClick(
                                                                                    onChange,
                                                                                    value
                                                                                )
                                                                            }
                                                                            style={{
                                                                                color: value
                                                                                    ? COLORS.LT_PEPPER
                                                                                    : COLORS.LT_MIDNIGHT25,
                                                                                cursor: 'pointer',
                                                                            }}
                                                                        />
                                                                    )}
                                                                />
                                                            </Box>
                                                            <div className={classes.alertContainer}>
                                                                {expense.errors &&
                                                                    expense.errors.length > 0 && (
                                                                        <ExpenseNeedsReviewAlert
                                                                            expense={expense}
                                                                        />
                                                                    )}
                                                            </div>
                                                            <Grid
                                                                container
                                                                direction="column"
                                                                className={
                                                                    classes.formGridContainer
                                                                }
                                                            >
                                                                <Grid item xs={12}>
                                                                    {!isSGA[index] && (
                                                                        <BookingDetailsForm
                                                                            index={index}
                                                                            expense={expense}
                                                                            divisions={divisions}
                                                                            mode={mode}
                                                                            setIsSGA={setIsSGA}
                                                                            originalExpenseEntries={
                                                                                originalExpenseEntries
                                                                            }
                                                                            onAssignmentChange={
                                                                                handleAssignmentChange
                                                                            }
                                                                            isSGA={isSGA}
                                                                            isViewOnly={
                                                                                isViewOnly ||
                                                                                isExpenseViewOnlyByStatus(
                                                                                    expense,
                                                                                    {
                                                                                        isClinicianReimEntryUser,
                                                                                    }
                                                                                )
                                                                            }
                                                                        />
                                                                    )}
                                                                </Grid>
                                                                <Grid item xs={12}>
                                                                    <PreCodedExpenseDetailsForm
                                                                        index={index}
                                                                        expense={expense}
                                                                        divisions={
                                                                            isSGA[index]
                                                                                ? divisionsSGA
                                                                                : divisions
                                                                        }
                                                                        mode={mode}
                                                                        setIsSGA={setIsSGA}
                                                                        isSGA={isSGA}
                                                                        expenseCategoriesSGA={
                                                                            expenseCategoriesSGA
                                                                        }
                                                                        expenseCategoriesNonSGA={
                                                                            expenseCategoriesNonSGA
                                                                        }
                                                                        allCreditCards={
                                                                            allCreditCards
                                                                        }
                                                                        isViewOnly={
                                                                            isViewOnly ||
                                                                            isExpenseViewOnlyByStatus(
                                                                                expense,
                                                                                {
                                                                                    isClinicianReimEntryUser,
                                                                                }
                                                                            )
                                                                        }
                                                                    />
                                                                </Grid>
                                                                {id &&
                                                                    (isExpenseSystemAdministratorUser ||
                                                                        isExpenseAdminUser) &&
                                                                    expense.importedData && (
                                                                        <Grid item xs={12}>
                                                                            <AdditionalExpenseDetails
                                                                                index={index}
                                                                                expense={expense}
                                                                                mode={mode}
                                                                                isViewOnly={
                                                                                    isViewOnly ||
                                                                                    isExpenseViewOnlyByStatus(
                                                                                        expense,
                                                                                        {
                                                                                            isClinicianReimEntryUser,
                                                                                        }
                                                                                    )
                                                                                }
                                                                            />
                                                                        </Grid>
                                                                    )}
                                                                <Grid item xs={12}>
                                                                    <ExpenseReceiptUploadForm
                                                                        index={index}
                                                                        expense={expense}
                                                                        originalExpenseEntries={
                                                                            originalExpenseEntries
                                                                        }
                                                                        mode={mode}
                                                                        isViewOnly={
                                                                            isViewOnly ||
                                                                            isExpenseViewOnlyByStatus(
                                                                                expense,
                                                                                {
                                                                                    isClinicianReimEntryUser,
                                                                                }
                                                                            )
                                                                        }
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12}>
                                                                    <InternalNotesForm
                                                                        index={index}
                                                                        expense={expense}
                                                                        mode={mode}
                                                                        originalExpenseEntries={
                                                                            originalExpenseEntries
                                                                        }
                                                                        statusesAllowed={
                                                                            statusesAllowed
                                                                        }
                                                                        isReimbursement={false}
                                                                        isViewOnly={
                                                                            isViewOnly ||
                                                                            isExpenseViewOnlyByStatus(
                                                                                expense,
                                                                                {
                                                                                    isClinicianReimEntryUser,
                                                                                }
                                                                            )
                                                                        }
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12}>
                                                                    <ReasonForSplittingForm
                                                                        index={index}
                                                                        expense={expense}
                                                                        originalExpenseEntries={
                                                                            originalExpenseEntries
                                                                        }
                                                                        mode={mode}
                                                                        isViewOnly={
                                                                            isViewOnly ||
                                                                            isExpenseViewOnlyByStatus(
                                                                                expense,
                                                                                {
                                                                                    isClinicianReimEntryUser,
                                                                                }
                                                                            )
                                                                        }
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        </ExpenseTabPanel>
                                                    ))}
                                                </div>
                                            )}
                                        </Grid>
                                        <Grid item md={4} sm={5} lg={3} xs={12}>
                                            <div className={classes.summary}>
                                                <ExpenseSummary
                                                    mode={mode}
                                                    setError={setError}
                                                    clearErrors={clearErrors}
                                                    setShowBalanceError={setShowBalanceError}
                                                />
                                                {showBalanceError && (
                                                    <Typography color="error">
                                                        {errors.balance.message}
                                                    </Typography>
                                                )}
                                            </div>
                                        </Grid>
                                    </Grid>
                                </form>
                            </Grid>
                            {hasErrors > 0 && (
                                <Grid container className={classes.alertContainer}>
                                    <Grid item sm={7} md={8} lg={9} xs={12}>
                                        <MuiAlert severity="error">
                                            Please review the highlighted fields above and correct
                                            the errors to submit your expense.
                                        </MuiAlert>
                                    </Grid>
                                </Grid>
                            )}
                            {!isViewOnly &&
                                (mode !== 'cc' || !(mode === 'cc' && isAllExpensePosted)) && (
                                    <Grid item xs={12}>
                                        <ExpenseButtons
                                            validateSubmit={validateSubmit}
                                            validateSave={validateSave}
                                            expensesPath={
                                                id
                                                    ? mode === 'pending-cc'
                                                        ? '/expenses/pending-cc/review'
                                                        : '/expenses/review'
                                                    : '/expenses'
                                            }
                                        />
                                    </Grid>
                                )}
                        </>
                    )}
                </Grid>
            </FormProvider>
            <Alert alert={alert} setAlert={setAlert} />
        </Container>
    );
};

export default withStyles(styles)(PreCoded);
